import { useEffect, useMemo, useRef, useState } from 'react';
import { useOrganization, useOrganizationList } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import { BiBuilding, BiChevronDown, BiCheck } from 'react-icons/bi';
import { fetchMyOrgs } from '../../api/onboardingApi';

// Custom replacement for Clerk's <OrganizationSwitcher>.
//
// Why we built our own instead of styling theirs: Clerk's widget renders
// a "Create Organisation" affordance on multiple sub-surfaces (the popover
// footer, the Manage Organisation modal, etc.) that can't all be reliably
// suppressed via `appearance.elements`. We need ZERO chance of that
// button appearing — not just "non-functional via webhook backstop". So
// we render the markup ourselves and use Clerk only as a data layer.
//
// Filters memberships down to MarginPal-known orgs only (via
// /api/Onboarding/MyOrgs). A user with a pre-existing personal Clerk org
// from another product won't see it here.
export default function OrgSwitcher() {
    const { organization: active } = useOrganization();
    const { isLoaded, userMemberships, setActive } = useOrganizationList({
        userMemberships: { infinite: true },
    });
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const [open, setOpen] = useState(false);
    const [knownOrgIds, setKnownOrgIds] = useState(null); // null = loading
    const [switching, setSwitching] = useState(false);
    const popoverRef = useRef(null);

    // Fetch the MarginPal-known org id list once on mount. The set is
    // small (one row per User entry per org) so caching across renders
    // via useState is fine. Refetch when the active org changes — that's
    // the cheapest signal that membership state may have changed.
    useEffect(() => {
        let cancelled = false;
        fetchMyOrgs()
            .then(ids => { if (!cancelled) setKnownOrgIds(new Set(ids)); })
            .catch(() => { if (!cancelled) setKnownOrgIds(new Set()); });
        return () => { cancelled = true; };
    }, [active?.id]);

    // Close the popover on outside click.
    useEffect(() => {
        if (!open) return;
        const handleClick = (e) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    const filteredMemberships = useMemo(() => {
        if (!isLoaded || !userMemberships?.data) return [];
        if (knownOrgIds === null) return []; // wait for filter list
        return userMemberships.data.filter(m => knownOrgIds.has(m.organization.id));
    }, [isLoaded, userMemberships, knownOrgIds]);

    const handleSwitch = async (orgId) => {
        if (orgId === active?.id) {
            setOpen(false);
            return;
        }
        setSwitching(true);
        try {
            await setActive({ organization: orgId });
            // Every cached query is keyed off the now-stale org. Wipe the
            // entire react-query cache so subsequent renders refetch
            // against the new tenant — anything narrower (key-by-key
            // invalidation) is fragile because new queries get added all
            // over the app and we'd have to remember each one. Then bounce
            // to /dashboard since the current URL may be a deep link to a
            // record (job, customer, quote) that doesn't exist in the new
            // org and would render a 404 panel.
            queryClient.clear();
            navigate('/dashboard', { replace: true });
        } finally {
            setSwitching(false);
            setOpen(false);
        }
    };

    if (!isLoaded || !active) {
        return (
            <div className="d-inline-flex align-items-center gap-2 text-muted small px-2">
                <Spinner size="sm" />
            </div>
        );
    }

    const hasMultiple = filteredMemberships.length > 1;

    return (
        <div className="org-switcher position-relative" ref={popoverRef}>
            <button
                type="button"
                className={`org-switcher__trigger d-inline-flex align-items-center gap-2 ${hasMultiple ? 'org-switcher__trigger--clickable' : ''}`}
                onClick={() => hasMultiple && setOpen(o => !o)}
                disabled={!hasMultiple && switching}
                aria-haspopup={hasMultiple ? 'menu' : undefined}
                aria-expanded={hasMultiple ? open : undefined}
            >
                <BiBuilding size={15} className="text-muted flex-shrink-0" />
                <span className="org-switcher__name fw-medium" title={active.name}>
                    {active.name}
                </span>
                {hasMultiple && (
                    <BiChevronDown
                        size={14}
                        className="text-muted flex-shrink-0"
                        style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 120ms ease' }}
                    />
                )}
            </button>
            {open && hasMultiple && (
                <div className="org-switcher__menu" role="menu">
                    <div className="org-switcher__heading">Switch organisation</div>
                    <div className="org-switcher__items">
                        {filteredMemberships.map(m => {
                            const isActive = m.organization.id === active.id;
                            return (
                                <button
                                    key={m.organization.id}
                                    type="button"
                                    role="menuitem"
                                    className={`org-switcher__item ${isActive ? 'org-switcher__item--active' : ''}`}
                                    onClick={() => handleSwitch(m.organization.id)}
                                    disabled={switching}
                                >
                                    <BiBuilding size={15} className={`flex-shrink-0 ${isActive ? 'text-primary' : 'text-muted'}`} />
                                    <span className="org-switcher__item-name" title={m.organization.name}>
                                        {m.organization.name}
                                    </span>
                                    {isActive && <BiCheck size={16} className="text-primary flex-shrink-0" />}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
